import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  IconButton,
  Grid,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Backdrop,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  AutoFixHigh,
  ContentCopy,
  FormatListBulleted,
  Notes,
  Bolt,
  DescriptionOutlined,
  ShortText,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

export default function Summarizer(): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [length, setLength] = useState<"short" | "medium" | "detailed">(
    "medium",
  );
  const [format, setFormat] = useState<"paragraph" | "bullets">("paragraph");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const handleSummarize = async () => {
    if (!inputText.trim()) return;
    setLoading(true);

    const prompt = `Summarize the following text into a ${length} ${format === "bullets" ? "list of key bullet points" : "concise paragraph"}: \n\n${inputText}`;

    try {
      const response = await axios.post(`${API_BASE_URL}/gemini-generate`, {
        prompt: prompt,
        model: "gemini-flash-latest",
      }, { timeout: 90000 });

      if (response.data && response.data.result) {
        setSummary(response.data.result);
      }
    } catch (error) {
      console.error("Summarization failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(summary);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Smart{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Summary
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Condense complex documents into clear, actionable summaries while
          preserving key information.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#030303" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: 520,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 3, flexGrow: 1 }}>
              <TextField
                fullWidth
                multiline
                placeholder="Paste the text you want to summarize here..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: "inherit",
                    fontSize: "15px",
                    lineHeight: 1.6,
                    color: isDarkMode ? "white" : "text.primary",
                    height: "100%",
                    alignItems: "flex-start",
                    "& textarea": { height: "100% !important" },
                  },
                }}
                sx={{ height: "100%" }}
              />
            </Box>
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
              >
                {inputText.split(/\s+/).filter((x) => x).length} words
              </Typography>
              <Button
                variant="contained"
                onClick={handleSummarize}
                disabled={loading || !inputText.trim()}
                startIcon={<Bolt />}
                sx={{
                  borderRadius: "10px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: 4,
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                Summarize
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            {/* Options Panel */}
            <Paper
              sx={{
                p: 2,
                borderRadius: "20px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#4b5563" : "text.secondary",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Length
                  </Typography>
                  <ToggleButtonGroup
                    value={length}
                    exclusive
                    onChange={(_, v) => v && setLength(v)}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        color: isDarkMode ? "#6b7280" : "text.secondary",
                        borderColor: isDarkMode
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.06)",
                        px: 2,
                      },
                    }}
                  >
                    <ToggleButton value="short">
                      <ShortText sx={{ mr: 0.5, fontSize: 18 }} /> Short
                    </ToggleButton>
                    <ToggleButton value="medium">Medium</ToggleButton>
                    <ToggleButton value="detailed">Detailed</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#4b5563" : "text.secondary",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Format
                  </Typography>
                  <ToggleButtonGroup
                    value={format}
                    exclusive
                    onChange={(_, v) => v && setFormat(v)}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        color: isDarkMode ? "#6b7280" : "text.secondary",
                        borderColor: isDarkMode
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.06)",
                        px: 2,
                      },
                    }}
                  >
                    <ToggleButton value="paragraph">
                      <Notes sx={{ mr: 0.5, fontSize: 18 }} /> Text
                    </ToggleButton>
                    <ToggleButton value="bullets">
                      <FormatListBulleted sx={{ mr: 0.5, fontSize: 18 }} />{" "}
                      Bullets
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Stack>
            </Paper>

            {/* Output Panel */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: "24px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                minHeight: 388,
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                  }}
                >
                  <LinearProgress
                    sx={{
                      height: 2,
                      bgcolor: "transparent",
                      "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" },
                    }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? "#9ca3af" : "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <DescriptionOutlined
                    fontSize="small"
                    sx={{ color: "#3b82f6" }}
                  />{" "}
                  Summary Result
                </Typography>
                <IconButton
                  onClick={handleCopy}
                  size="small"
                  disabled={!summary}
                  sx={{ color: isDarkMode ? "#6b7280" : "text.secondary" }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
                <Typography
                  sx={{
                    fontSize: "15px",
                    lineHeight: 1.7,
                    color: summary
                      ? isDarkMode
                        ? "white"
                        : "text.primary"
                      : isDarkMode
                        ? "#4b5563"
                        : "text.secondary",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {summary ||
                    "Your summary will appear here after processing..."}
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ color: "white", fontWeight: 500 }}>
          Summarizing your text...
        </Typography>
      </Backdrop>
    </Box>
  );
}
